import React, { useEffect, useMemo, useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, CalendarDays, Clock } from "lucide-react";
import api from "@/lib/api";
import { trackConversion } from "@/lib/gtag";
import { formatApiError } from "@/context/AuthContext";

const PROBLEM_TYPES = [
  { v: "personale", l: "Debiti personali" },
  { v: "aziendale", l: "Debiti aziendali" },
  { v: "mutuo", l: "Mutuo / casa a rischio" },
  { v: "fiscale", l: "Cartelle esattoriali" },
];

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function toISO(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), g = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${g}`;
}

export default function Booking() {
  usePageMeta(
    "Prenota una Chiamata Gratuita | Sdebitiamoci Srl",
    "Scegli data e ora: chiamata conoscitiva gratuita di 30 minuti per capire se il tuo debito è trattabile con il saldo e stralcio."
  );
  const days = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < 30 && out.length < 18; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (d.getDay() !== 0) out.push(d);
    }
    return out;
  }, []);

  const [selDate, setSelDate] = useState(toISO(days[0]));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selTime, setSelTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", problem_type: "personale", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingSlots(true);
    setSelTime(null);
    api.get(`/appointments/slots?date=${selDate}`)
      .then(r => { if (alive) setSlots(r.data.slots); })
      .catch(() => { if (alive) setSlots([]); })
      .finally(() => { if (alive) setLoadingSlots(false); });
    return () => { alive = false; };
  }, [selDate]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const slotClass = (s) => {
    if (!s.available) return "border-[#E5E7EB] text-[#C3CEDD] line-through cursor-not-allowed";
    if (selTime === s.time) return "bg-[#14417B] text-white border-[#14417B]";
    return "border-[#E5E7EB] text-[#2d6fc4] hover:border-[#14417B]";
  };

  const submitLabel = () => {
    if (sending) return "Prenotazione in corso…";
    if (selTime) return `Conferma — ${fmtDate(selDate)} ore ${selTime}`;
    return "Seleziona un orario";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selTime) { toast.error("Seleziona un orario"); return; }
    setSending(true);
    try {
      const r = await api.post("/appointments", { ...form, date: selDate, time: selTime });
      setDone(r.data);
      trackConversion("booking");
      toast.success("Prenotazione confermata");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Errore nella prenotazione";
      toast.error(msg);
      if (err.response?.status === 409) {
        api.get(`/appointments/slots?date=${selDate}`).then(r => setSlots(r.data.slots));
        setSelTime(null);
      }
    } finally { setSending(false); }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  };

  if (done) {
    return (
      <div className="pt-32 pb-24" data-testid="booking-page">
        <section className="container-editorial max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="border border-[#E5E7EB] bg-[#F1F5F9] rounded-lg p-10 md:p-16"
            data-testid="booking-success"
          >
            <CheckCircle2 size={40} className="text-[#2d6fc4]" strokeWidth={1.5}/>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-tight text-[#2d6fc4]">Chiamata prenotata.</h2>
            <p className="mt-4 text-lg text-[#333333]">
              Ti chiameremo <strong>{fmtDate(done.date)}</strong> alle <strong>{done.time}</strong> al numero <strong>{done.phone}</strong>.
            </p>
            <p className="mt-6 text-sm text-[#333333]">
              La chiamata è gratuita e senza impegno. Se hai un imprevisto, scrivici su WhatsApp per spostare l'appuntamento.
            </p>
          </motion.div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="booking-page">
      <section className="container-editorial grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Prenota una chiamata</p>
          <h1 className="font-serif text-4xl md:text-6xl leading-[0.9] mt-4 text-[#2d6fc4]">
            Scegli tu<br/><em className="italic text-[#2d6fc4]">data e ora.</em>
          </h1>
          <p className="mt-6 text-[#333333] max-w-sm">
            Una chiamata conoscitiva di 30 minuti, gratuita e senza impegno, per capire se il tuo caso è trattabile con il saldo e stralcio.
          </p>
          <div className="mt-8 border-t border-[#E5E7EB] pt-6 space-y-2 text-sm text-[#333333]">
            <p className="flex items-center gap-2"><CalendarDays size={15}/> Lun–Ven 9:00–18:00 · Sab 9:00–13:00</p>
            <p className="flex items-center gap-2"><Clock size={15}/> Durata: 30 minuti</p>
          </div>
        </div>

        <div className="col-span-12 md:col-span-8">
          <form onSubmit={submit} className="border border-[#E5E7EB] bg-white rounded-lg p-8 md:p-10 space-y-8" data-testid="booking-form">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4] mb-3">1. Scegli il giorno</p>
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="date-picker">
                {days.map(d => {
                  const iso = toISO(d);
                  const active = iso === selDate;
                  return (
                    <button
                      key={iso} type="button" onClick={() => setSelDate(iso)}
                      data-testid={`date-btn-${iso}`}
                      className={`shrink-0 w-16 py-3 rounded-lg border text-center ${active ? "bg-[#1a4a8a] text-white border-[#0B1F3F]" : "border-[#E5E7EB] text-[#2d6fc4] hover:border-[#14417B]"}`}
                      style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}
                    >
                      <span className="block font-mono text-[10px] uppercase tracking-wider opacity-70">{DAY_NAMES[d.getDay()]}</span>
                      <span className="block font-serif text-xl leading-tight">{d.getDate()}</span>
                      <span className="block font-mono text-[10px] uppercase opacity-70">{MONTHS[d.getMonth()]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4] mb-3">2. Scegli l'orario</p>
              {loadingSlots && <p className="text-sm text-[#333333]" data-testid="slots-loading">Caricamento orari…</p>}
              {!loadingSlots && slots.filter(s => s.available).length === 0 && (
                <p className="text-sm text-[#333333]" data-testid="no-slots">Nessun orario disponibile per questo giorno. Prova un altro giorno.</p>
              )}
              {!loadingSlots && slots.filter(s => s.available).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="slot-grid">
                  {slots.map(s => (
                    <button
                      key={s.time} type="button" disabled={!s.available}
                      onClick={() => setSelTime(s.time)}
                      data-testid={`slot-btn-${s.time}`}
                      className={`py-2.5 rounded-lg border text-sm font-mono ${slotClass(s)}`}
                      style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4] mb-3">3. I tuoi dati</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-[#333333] mb-1">Nome e cognome *</label>
                  <input required data-testid="booking-input-name" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] outline-none" value={form.name} onChange={set("name")}/>
                </div>
                <div>
                  <label className="block text-xs text-[#333333] mb-1">Telefono *</label>
                  <input required type="tel" data-testid="booking-input-phone" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] outline-none" value={form.phone} onChange={set("phone")}/>
                </div>
                <div>
                  <label className="block text-xs text-[#333333] mb-1">Email *</label>
                  <input required type="email" data-testid="booking-input-email" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] outline-none" value={form.email} onChange={set("email")}/>
                </div>
                <div>
                  <label className="block text-xs text-[#333333] mb-1">Tipo di problema *</label>
                  <select data-testid="booking-select-problem" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] outline-none" value={form.problem_type} onChange={set("problem_type")}>
                    {PROBLEM_TYPES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-[#333333] mb-1">Note (facoltativo)</label>
                  <textarea rows={2} data-testid="booking-input-notes" className="w-full bg-transparent border-b border-[#E5E7EB] py-3 focus:border-[#14417B] outline-none resize-none" value={form.notes} onChange={set("notes")}/>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-4 pt-2">
              <button
                type="submit" disabled={sending || !selTime}
                data-testid="booking-submit-btn"
                className="btn-terracotta inline-flex items-center justify-center px-8 py-3.5 rounded-lg text-sm tracking-wide disabled:opacity-50"
              >
                {submitLabel()}
              </button>
              <p className="text-xs text-[#333333]">Gratuita · Senza impegno · Riservata</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
